import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GriddStyleSheetEditor } from "@gridd/embed/react";
import {
  GriddLibrary,
  type GriddSlideDocument,
  type GriddStyleSheetData as LibraryStyleSheetData,
  type LibraryPick,
} from "@gridd/library-embed/react";
import {
  ChevronDown,
  Eye,
  Film,
  ChartSpline,
  GanttChartSquare,
  Library,
  Link2,
  Paperclip,
  List,
  Images,
  Package,
  Palette,
  PenLine,
  Heading1,
  Plus,
  Sparkles,
  Stamp,
  X,
} from "lucide-react";
import { useDefaultLayout } from "react-resizable-panels";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { AiTerminal } from "@/components/AiTerminal";
import { TopBar } from "@/components/deck/TopBar";
import { Slide, type SlideData } from "@/components/deck/Slide";
import { PresentationView } from "@/components/deck/PresentationView";
import { TransitionPanel } from "@/components/deck/TransitionPanel";
import { DiagramPalette } from "@/components/deck/DiagramPalette";
import { ChartPanel } from "@/components/deck/ChartPanel";
import {
  ChartEditingProvider,
  useChartEditing,
} from "@/components/deck/chartEditing";
import { DiagramPanel } from "@/components/deck/DiagramPanel";
import {
  DiagramEditingProvider,
  useDiagramEditing,
} from "@/components/deck/diagramEditing";
import { GanttPanel } from "@/components/deck/GanttPanel";
import {
  GanttEditingProvider,
  useGanttEditing,
} from "@/components/deck/ganttEditing";
import { LogoPanel } from "@/components/deck/LogoPanel";
import { NuggetPanel } from "@/components/deck/NuggetPanel";
import { PrintView } from "@/components/deck/PrintView";
import {
  SnapshotRegisterProvider,
  useSnapshotRegistry,
} from "@/components/deck/snapshotRegistry";
import {
  ThumbnailScheduleProvider,
  useThumbnails,
} from "@/components/deck/thumbnailStore";
import {
  OffscreenSlideCapturer,
  type SlideThumb,
} from "@/components/deck/slideThumbnails";
import { Footer } from "@/components/deck/Footer";
import { DesktopTabs } from "@/desktop/DesktopTabs";
import { useAuth } from "@/auth/useAuth";
import { useOnline } from "@/hooks/useOnline";
import { isAiTerminalAvailable, isElectron } from "@/lib/runtimeEnv";
import { desktopShell } from "@/lib/desktopBridge";
import { exportFileName, saveFile } from "@/lib/saveFile";
import {
  buildDeckHtml,
  collectDiagramSvgs,
  collectImageDataUrls,
  collectLogoDataUrl,
  downloadHtml,
} from "@/lib/htmlExport";
import { withStudioPresets } from "@/lib/studio-presets";
import { GRIDD_EMBED_BASE_URL, GRIDD_LIBRARY_BASE_URL } from "@/lib/config";
import { exportDeckFile } from "@/lib/deckFile";
import { collectChartSvgs } from "@/lib/chartSvg";
import { mergeChartStyles } from "@/lib/stylesheet-chart";
import { collectGanttImages } from "@/lib/ganttImage";
import {
  getNugget,
  listNuggets,
  putDeckThumb,
  putNuggetThumb,
} from "@/lib/idb";
import { materializeFrom, slideUsingNugget } from "@/lib/nugget";
import {
  breakSlideLink,
  materializeNuggetIntoSlide,
  saveSlideAsNugget,
} from "@/lib/nuggetActions";
import { subscribeDeckMessages } from "@/lib/deckChannel";
import { libraryDocToGriddDocument } from "@/lib/libraryGridd";
import { dropContentArtifacts } from "@/lib/contentCleanup";
import {
  EVIDENCE_SOFT_MAX,
  evidenceChildIds,
  groupSlides,
  levelOf,
  STORYLINE_SOFT_MAX,
} from "@/lib/pyramid";
import { slideNumbering } from "@/lib/slide-numbering";
import { SLIDE_H, SLIDE_W } from "@/lib/slide-size";
import { cn } from "@/lib/utils";
import { store } from "@/store";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  addSlide as addSlideAction,
  deleteSlide as deleteSlideAction,
  moveSlide as moveSlideAction,
  purgeSlide as purgeSlideAction,
  restoreSlide as restoreSlideAction,
  setSlideLevel as setSlideLevelAction,
  setStyleSheet as setStyleSheetAction,
  updateSlide as updateSlideAction,
} from "@/store/documentSlice";
import { griddDocCreated } from "@/store/griddSlice";
import { nuggetApplied } from "@/store/documentSlice";
import {
  draftAbandoned,
  draftChanged,
  draftCommitted,
} from "@/store/styleDraftSlice";
import type { LucideIcon } from "lucide-react";
import type { GriddSnapshot } from "@gridd/embed/react";
import type {
  GriddStyleSheetData,
  SlideContents,
  SlideLevel,
} from "@/types/document";
import type { NuggetIndexEntry } from "@/types/nugget";
import templateLibrary from "@/assets/template-library.png";

// === The right-panel set, defined ONCE (§17.4.5 added the seventh) ===
//
// Two things render this list: the 48px icon rail on the right edge when nothing is open, and
// the switcher in an open panel's own header. They used to be one list and one hard-coded
// column, which meant that opening any panel replaced the rail entirely — so switching from
// the style sheet to the Library was close, hunt for the icon that had just moved, click. The
// panels are peers; moving between them should cost one click, and it now does.
//
// `needs` marks the three panels that are about CONTENT rather than about the deck (§21/§8.1,
// §26/§8, §15/§7.2): they edit a diagram, a gantt or a chart, so until one is SELECTED they
// are **disabled**, with the tooltip saying what to do instead. They used to be offered
// unconditionally and explain themselves once opened, which spends a click and a panel to say
// "not here" — and made the rail read as five live tools and three dead ones.
//
// All three now answer the same question ("which one is the user in?") from their own
// selection event — see `usePanelReach`.
//
// `foot: true` pins the assistant to the bottom of the rail: it is not a deck-authoring tool
// like the six above it, and separating it keeps that reading.
type PanelId =
  | "style"
  | "library"
  | "nuggets"
  | "transition"
  | "logo"
  | "diagram"
  | "gantt"
  | "chart"
  | "assistant";

const PANEL_DEFS: {
  id: PanelId;
  label: string;
  icon: LucideIcon;
  foot?: boolean;
  // Shown instead of `label` while the panel is disabled: what the user has to do to make it
  // live. Present on exactly the panels `usePanelReach` can turn off.
  needs?: string;
}[] = [
  { id: "style", label: "Edit style sheet", icon: Palette },
  { id: "library", label: "Gridd Library", icon: Library },
  { id: "nuggets", label: "Knowledge nuggets", icon: Package },
  { id: "transition", label: "Slide transitions", icon: Film },
  { id: "logo", label: "Deck logo", icon: Stamp },
  {
    id: "diagram",
    label: "Diagram",
    icon: PenLine,
    needs: "Click a diagram on a slide to edit it here",
  },
  {
    id: "gantt",
    label: "Gantt",
    icon: GanttChartSquare,
    needs: "Click a gantt on a slide to edit it here",
  },
  // §15/§7.1 The chart's style window. Grouped after the deck-level four with the diagram and
  // gantt panels because like them it is about the content.
  //
  // **Its lower half is the deck's chart style, and the gate puts that behind a chart click.**
  // A deliberate cost, not an oversight: a deck-wide setting reached by selecting one chart is
  // odd, but the alternative — an always-live Chart button — is a rail entry that opens onto
  // "select a chart" on every slide of every deck, which is the thing this gate exists to stop.
  // If the deck style outgrows the selection panel it wants to move up to the style sheet
  // editor, not to relax this.
  {
    id: "chart",
    label: "Chart",
    icon: ChartSpline,
    needs: "Click a chart on a slide to edit it here",
  },
  { id: "assistant", label: "AI Assistant", icon: Sparkles, foot: true },
];

// === Which content panels have something to act on (the disable rule) ===
//
// **Must be called inside the three editing providers** — `DeckEditor` renders them, so it
// cannot call this in its own body; that is why the switcher and the rail below are components
// rather than the plain JSX values they used to be.
//
// All three now mean the same thing — **the user has selected one of these** — and each block
// reports it from its own selection event rather than from having mounted:
//
//   • **diagram** — `DiagramBlock` publishes a selection on click (§21/§6.2) and always did.
//     It is sticky: nothing calls `select(null)`, so the button stays live once a diagram has
//     been clicked. Harmless, and honest: the deck demonstrably has one.
//   • **gantt** — SVAR's own `select-task`, plus a pointer down on the block for the chrome
//     around it (§26/§8.6). Mounting only REGISTERS the api now.
//   • **chart** — a pointer down on the block, which for a renderer we own is the selection
//     event; there is no library callback to wait for (§15/§7.2). Mounting no longer claims.
//
// The mount-claims they all started with made this gate lie in one specific way worth
// remembering: the editor mounts EVERY slide at once, so "a chart has published itself" was
// true from the first render of any deck containing a chart anywhere — the button was live on
// every slide and the panel edited whichever one initialised last.
//
// Everything not named here is deck-level and always available.
function usePanelReach(): (id: PanelId) => boolean {
  const { selectedDocId, editingDocId } = useDiagramEditing();
  const { activeDocId: ganttDocId } = useGanttEditing();
  const { activeDocId: chartDocId } = useChartEditing();
  return (id) => {
    if (id === "diagram") return Boolean(selectedDocId ?? editingDocId);
    if (id === "gantt") return ganttDocId != null;
    if (id === "chart") return chartDocId != null;
    return true;
  };
}

// EVERY panel's icon, in `PANEL_DEFS` order, as the open panel's header — the open one
// included and marked blue.
//
// It used to drop the open panel from the row and name it in a title beside it, which cost
// more than it looked: **the icons moved.** Which slot the Library sits in depended on which
// panel happened to be open, so the one thing a toolbar has to offer — hitting the same target
// twice in a row without reading it — was exactly what it could not. Keeping the whole row
// fixed and colouring the current one is the trade the other way: one slot spent saying where
// you are, and every other slot always in the same place.
//
// That also makes the title redundant, which is why it is gone and this row IS the header: the
// blue icon says which panel this is, its tooltip spells it out, and the panel's own contents
// were always the real answer anyway. The width this frees is the point — the pane resizes
// down to 320px, where a title plus eight destinations plus a close button had nothing left to
// give.
//
// A component rather than a JSX value in `DeckEditor` for one reason: `usePanelReach` reads
// the three editing contexts, and `DeckEditor` is the component that PROVIDES them — calling
// the hooks in its body would read the inert defaults and disable all three panels forever.
function PanelSwitcher({
  panel,
  onPick,
}: {
  panel: PanelId | null;
  // Switching straight from the style sheet discards a dirty draft, so this goes through
  // `switchPanel`'s confirm — the same question the X asks, for the same reason.
  onPick: (id: PanelId) => void;
}) {
  const inReach = usePanelReach();
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex min-w-0 items-center">
        {PANEL_DEFS.filter(
          (def) => def.id !== "assistant" || isAiTerminalAvailable(),
        ).map((def) => {
          const isOpen = def.id === panel;
          // The open panel is never disabled under the user: its content may have gone out of
          // reach (the slide scrolled away, the diagram was deselected) while they are still
          // reading it, and greying out the icon that says WHERE THEY ARE would be the panel
          // announcing it no longer exists.
          const enabled = isOpen || inReach(def.id);
          return (
            <Tooltip key={def.id}>
              {/* The span is load-bearing: a disabled button emits no pointer events, so a
                  tooltip anchored directly to it never opens — and the tooltip is the only
                  place the reason for the disabling is written. */}
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={!enabled}
                    className={cn(
                      "h-7 w-7 shrink-0",
                      isOpen
                        ? "text-blue-600 hover:text-blue-600"
                        : "text-slate-500 hover:text-slate-900",
                    )}
                    onClick={() => {
                      // Clicking the panel you are already in is a no-op rather than a
                      // re-open: it must not ask the discard question, and it must never
                      // close the panel (the no-click-to-close rule).
                      if (!isOpen) onPick(def.id);
                    }}
                    aria-label={def.label}
                    aria-pressed={isOpen}
                  >
                    <def.icon className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {enabled ? def.label : (def.needs ?? def.label)}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

// The same set as `PanelSwitcher`, as the 48px rail on the right edge when nothing is open —
// bigger icons, tooltips on the left, and the assistant pushed to the foot. A component for
// the same reason the switcher is one: `usePanelReach` has to run inside the editing
// providers, which `DeckEditor` renders rather than sits inside.
function PanelRail({ onPick }: { onPick: (id: PanelId) => void }) {
  const inReach = usePanelReach();
  return (
    <TooltipProvider delayDuration={200}>
      <aside className="flex h-full w-12 shrink-0 flex-col items-center gap-2 border-l border-slate-200 bg-white py-3">
        {PANEL_DEFS.filter(
          // The assistant is only offered where the terminal can actually reach the user's
          // local model — the desktop shell or a localhost origin, never the deployed website
          // (runtimeEnv / TODO §Electron).
          (def) => def.id !== "assistant" || isAiTerminalAvailable(),
        ).map((def) => {
          const enabled = inReach(def.id);
          return (
            <Tooltip key={def.id}>
              {/* See `PanelSwitcher`: the span is what keeps the tooltip reachable on a
                  disabled button, and the tooltip carries the reason. */}
              <TooltipTrigger asChild>
                <span className={cn("inline-flex", def.foot && "mt-auto")}>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={!enabled}
                    onClick={() => onPick(def.id)}
                    aria-label={def.label}
                  >
                    <def.icon className="h-5 w-5" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="left">
                {enabled ? def.label : (def.needs ?? def.label)}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </aside>
    </TooltipProvider>
  );
}

// What one slide needs on screen beyond its own 16:9 canvas: its toolbar row (slide number +
// slide actions) and the column's vertical padding.
const SLIDE_CHROME_H = 112;
// Below this, shrinking stops being helpful — the slide is unreadably small and scrolling is
// the better answer. A very short window therefore scrolls, exactly as it did before.
const MIN_SLIDE_W = 640;

export function DeckEditor({
  readOnly = false,
  takingOver = false,
  onTakeOver,
}: {
  // §12.5.2 Another page holds this deck's write lock, so this one is a viewer: the
  // persistence listener drops every write, and the UI must say so rather than let the user
  // type into a deck that will not remember any of it.
  readOnly?: boolean;
  takingOver?: boolean;
  onTakeOver?: () => void;
} = {}) {
  const dispatch = useAppDispatch();
  const slides = useAppSelector((s) => s.document.slides);
  // Deck title feeds the exported HTML's <title> and download filename (§HTML export).
  const deckTitle = useAppSelector((s) => s.document.title);
  // The open deck (§12.1) — the cascade key for assets and the deck-index thumbnail.
  const deckId = useAppSelector((s) => s.document.id);

  // Fullscreen presentation (TODO §5). This is the *only* meaning of `presenting` now — a
  // dedicated overlay (`PresentationView`), not an inline read-only mode. The editor stays
  // mounted underneath it, so exiting restores the scroll position with no re-render churn.
  const [presenting, setPresenting] = useState(false);
  // Print-to-PDF mode (TODO §6). While true, `PrintView` mounts a body-level print layer
  // and drives `window.print()`; the print stylesheet hides the editor. Login gating
  // (§9.4) is deferred until Firebase auth lands.
  const [printing, setPrinting] = useState(false);
  // Grid snapshots captured for the current present/print overlay (§5.5/§6.6.4), keyed by
  // griddDocId. The registry collects live editor embed handles; `requestSnapshots` pulls the
  // images. Both overlays reuse this — they never run at once — and each clears it on exit.
  const [snapshots, setSnapshots] = useState<Record<string, GriddSnapshot>>({});
  const { register, requestSnapshots } = useSnapshotRegistry();
  // Deferred grid snapshots (griddDocId→snapshot), captured off the live editor embeds and fed
  // to the full-slide thumbnail capturer below in place of the cross-origin iframe.
  const { thumbnails, scheduleThumbnail } = useThumbnails(requestSnapshots);
  // The slide-nav view: "thumbnail" (full-slide images) or "outline" (numbered titles). A
  // pure-UI viewport pref (§1.2) — persisted to localStorage, never into DeckDocument.
  // §21/§7.1 The left panel's MODE — Slides or Elements. A tab switch on the existing nav
  // panel, never a replacement: it is auto-selected on entering diagram edit and restored on
  // exit, and slide selection and scroll position survive the round trip because the Slides
  // view is simply hidden, not unmounted (`hidden` on its container rather than a branch).
  // There is no horizontal room for a third panel at the deck's `minSize={480}`, and a
  // floating palette over a canvas someone is drawing on is the worst of both.
  const [navTab, setNavTab] = useState<"slides" | "elements">("slides");
  const [navMode, setNavMode] = useState<"thumbnail" | "outline">(() => {
    const saved =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("deck-nav-mode")
        : null;
    return saved === "outline" ? "outline" : "thumbnail";
  });
  useEffect(() => {
    localStorage.setItem("deck-nav-mode", navMode);
  }, [navMode]);
  // Full-slide thumbnail cache, keyed by slide id (see slideThumbnails.tsx). Filled by the
  // offscreen capturer; the nav's thumbnail view renders these PNGs.
  const [slideThumbs, setSlideThumbs] = useState<Record<string, SlideThumb>>(
    {},
  );
  const handleSlideCaptured = useCallback(
    (slideId: string, thumb: SlideThumb) => {
      setSlideThumbs((prev) => ({ ...prev, [slideId]: thumb }));
    },
    [],
  );
  // The right panel modes (§8.4.1 library, §10.3 transition, §AI terminal assistant). All obey
  // the no-click-to-close rule (explicit X only).
  //
  // `?panel=library` opens straight into the Library: it is how "New document → from the
  // Gridd Library" (§12.4.3) hands off, since that flow has to mint the deck and navigate
  // before there is an editor to open a panel in. Read once, at mount — it seeds initial
  // state rather than driving it, so closing the panel doesn't fight the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  // The open panel, or null. Every one of them obeys the no-click-to-close rule (explicit X
  // only), and the set itself is `PANEL_DEFS` above — which is also what draws the rail and
  // the in-header switcher, so a panel added there needs nothing here but a body below.
  const [panel, setPanel] = useState<PanelId | null>(() =>
    !readOnly && searchParams.get("panel") === "library" ? "library" : null,
  );
  // Consumed — drop it from the URL so a reload (or a shared link) doesn't keep re-opening
  // a panel the user has since closed. Replaces rather than pushes: this is not a step the
  // Back button should walk through.
  useEffect(() => {
    if (!searchParams.has("panel")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("panel");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  // Deck-level presentation transition (§10) — fed to the fullscreen presentation.
  const transition = useAppSelector((s) => s.document.transition);
  // §8.3 The studio's auth session feeds the library its Firebase ID token. `getIdToken`
  // covers on-demand refresh (§8.3.3); `idToken` is the eager copy pushed on identity
  // change; `promptSignIn` opens the shared sign-in dialog for the library's premium-while-
  // signed-out prompt (§8.3.5/§9.2).
  // §14.6.3/§14.6.5 `tokenFresh` and `withinOfflineGrace` are what the library panel needs to
  // say something honest when there is no network: "signed in" stays true offline, so
  // `status` alone cannot tell the user why a premium slide will not open.
  const {
    idToken,
    getIdToken,
    promptSignIn,
    status: authStatus,
    tokenFresh,
    withinOfflineGrace,
  } = useAuth();
  // Only used to word the library panel's offline line — never to gate anything (§14.6.5).
  const online = useOnline();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Height of the scrolling canvas column, measured. Slides are authored on a fixed logical
  // canvas and scaled to the available WIDTH (§1.4), which is right until the window is short
  // — then a full-width slide is taller than the column and its bottom half, along with the
  // controls under it, sits below the fold. So the width is capped by the height too, and one
  // slide plus its own chrome always fits what is left.
  const [canvasHeight, setCanvasHeight] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setCanvasHeight(el.clientHeight);
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    measure();
    return () => observer.disconnect();
  }, []);

  const slideMaxWidth =
    canvasHeight > 0
      ? Math.min(
          SLIDE_W,
          Math.max(
            MIN_SLIDE_W,
            (canvasHeight - SLIDE_CHROME_H) * (SLIDE_W / SLIDE_H),
          ),
        )
      : SLIDE_W;

  // §8.0.1 Currently-selected slide — pure-UI interaction state (like `presenting`/`panel`),
  // NOT deck content: it never enters DeckDocument and is never persisted. Drives the bold
  // nav title (§8.0.3) and is the copy target for the Library (§8.0.4).
  const [selectedSlideId, setSelectedSlideId] = useState<string | null>(null);
  // §8.0.2 Timestamp of the last explicit interaction (focus/edit/nav-click). Scroll-driven
  // center selection stands down for ~1s afterwards so it can't override a fresh selection —
  // in particular the smooth scroll that a nav click itself triggers.
  const lastInteractionRef = useRef(0);
  // Latest active-slide ids, kept fresh for the scroll handler so it needn't re-subscribe.
  const activeIdsRef = useRef<string[]>([]);

  const selectSlide = (id: string) => {
    lastInteractionRef.current = Date.now();
    setSelectedSlideId(id);
  };

  // Panel sizes are pure-UI viewport prefs (§1.2) — persisted to localStorage, never into
  // DeckDocument. `panelIds` lets the layout survive the right "side" panel being
  // conditionally mounted. `react-resizable-panels@4` has no `autoSaveId`; this hook is
  // the v4 equivalent, returning the props to feed the group.
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: "deck-editor-panels",
    storage: typeof localStorage !== "undefined" ? localStorage : undefined,
    panelIds: ["nav", "main", "side"],
  });

  // Committed sheet feeds the editor (never the live draft — TODO §10.2/§10.7).
  const deckLogo = useAppSelector((s) => s.document.logo);
  // §21/§11.2 The deck's diagram bodies, for the HTML export's own render pass.
  const diagramDocs = useAppSelector((s) => s.diagram);
  // §17.4.3/§17.4.4 …and the grid bodies, for the two nugget actions that COPY a slide's
  // bodies between owners. They read Redux rather than IndexedDB deliberately: the store
  // holds what the user is looking at, the database holds whatever the last ~400ms debounce
  // landed, and copying the latter silently drops the last half-second of typing.
  const griddDocs = useAppSelector((s) => s.gridd);
  // §26/§9.2 The same, for the gantt bake.
  const ganttDocs = useAppSelector((s) => s.gantt);
  // §15/§9.2 …and for the chart bake, which serializes SVG rather than rasterizing.
  const chartDocs = useAppSelector((s) => s.chart);
  const committedStyleSheet = useAppSelector((s) => s.document.styleSheet);
  // ...but the *editor* gets it with SlideCraft's own Title/Subtitle/Source presets folded
  // in (§7.4), which is what puts those three rows in the panel for the user to style. The
  // grids and the library keep the deck's sheet untouched — those presets mean nothing to
  // Gridd. Identity is stable per committed sheet (the injector caches), so this doesn't
  // re-push into the editor iframe on every render.
  const editableStyleSheet = withStudioPresets(committedStyleSheet);
  // Dirtiness is exactly `draft !== null` host-side (TODO §10.7).
  const styleDirty = useAppSelector((s) => s.styleDraft.draft !== null);

  const addSlide = () => dispatch(addSlideAction());
  // §18.2.2 Insert below: same reducer, given the anchor. The new slide is selected and
  // scrolled to, the way a library pick is (§8.4.2) — the point of inserting in the middle
  // of a long deck is to start editing there, not to hunt for where it landed.
  //
  // §18.2.2 The new slide inherits the ANCHOR's level. "+" on an evidence slide adds a
  // sibling rather than splitting its parent's block in two, and "+" on a backup slide adds a
  // backup — a storyline slide spliced into the middle of the backup suffix would be
  // partitioned straight back out to the end by `normalizePyramid` and appear somewhere the
  // user never clicked. "Add evidence below" is the explicit way down a level.
  const addSlideAfter = (afterId: string, level?: SlideLevel) => {
    const anchor = slides.find((s) => s.id === afterId);
    const added = dispatch(
      addSlideAction({ afterId, level: level ?? (anchor && levelOf(anchor)) }),
    );
    const newId = added.payload.id;
    selectSlide(newId);
    // Let the new slide mount before bringing it into view.
    setTimeout(() => scrollTo(newId), 50);
  };
  // §18.2.8 The backup divider is an ORDINARY slide titled *Backups*, not a new kind and not
  // an `isSectionCover` flag: the divider look comes from the deck's own stylesheet the way
  // any other title slide does. Appending it makes it the head of the suffix, because a
  // suffix is where `normalizePyramid` puts every backup anyway.
  const addBackupSection = () => {
    const added = dispatch(
      addSlideAction({ level: "backup", title: "Backups" }),
    );
    selectSlide(added.payload.id);
    setTimeout(() => scrollTo(added.payload.id), 50);
  };
  // …and the plain one, at the foot of the suffix. `addSlide` cannot express this: appending
  // a STORYLINE slide lands it in front of the backups, because that is where
  // `normalizePyramid` puts every non-backup slide. Which is the right default — the deck is
  // the thing you are usually writing — but it leaves "one more backup" with no button, and
  // demoting a slide you just added is two clicks for the commonest thing you do down here.
  const addBackupSlide = () => {
    const added = dispatch(addSlideAction({ level: "backup" }));
    selectSlide(added.payload.id);
    setTimeout(() => scrollTo(added.payload.id), 50);
  };
  // The evidence slides immediately behind `id` in the active order — which is exactly what
  // this slide inherits the moment it becomes a storyline slide itself.
  const evidenceChildIdsAfterPromotion = (id: string) => {
    const active = slides.filter((s) => !s.archived);
    const at = active.findIndex((s) => s.id === id);
    let n = 0;
    for (let i = at + 1; i < active.length; i++) {
      if (levelOf(active[i]) !== "evidence") break;
      n++;
    }
    return n;
  };
  // §18.2.1 Promoting an evidence slide adopts the evidence that followed it. That is the
  // right reading — the claim those slides supported is not being made any more — but it is a
  // change the user did not type, so it is said out loud rather than left for them to notice
  // in the outline.
  const setSlideLevel = (id: string, level: SlideLevel) => {
    const slide = slides.find((s) => s.id === id);
    if (!slide) return;
    const adopted =
      levelOf(slide) === "evidence" && level === "storyline"
        ? evidenceChildIdsAfterPromotion(id)
        : 0;
    dispatch(setSlideLevelAction({ id, level }));
    if (adopted > 0) {
      toast.info(
        `${adopted} evidence slide${adopted === 1 ? "" : "s"} now sit${adopted === 1 ? "s" : ""} under this one`,
      );
    }
  };
  // §19.1.2 A cover goes at position 0 — `afterId` cannot say that, so the reducer takes
  // `atStart`. `layout` rides along in the same dispatch so the slide is never briefly a
  // normal one (which would flash a page number and shift every number below it).
  const addCover = () => {
    const added = dispatch(addSlideAction({ atStart: true, layout: "cover" }));
    selectSlide(added.payload.id);
    setTimeout(() => scrollTo(added.payload.id), 50);
  };
  const updateSlide = (id: string, patch: Partial<SlideData>) =>
    dispatch(updateSlideAction({ id, patch }));
  // §18.2.4 Archiving a storyline slide takes its evidence with it, after asking once.
  // Evidence is *defined* by its parent, so evidence left behind is evidence attributed to a
  // claim nobody is making any more — which is why OK (the default button) is the "yes"
  // branch. Cancel still archives just this slide; the evidence then re-parents to whatever
  // storyline slide now precedes it, which is the adjacency rule doing what it says.
  const deleteSlide = (id: string) => {
    const children = evidenceChildIds(slides, id);
    if (children.length === 0) {
      dispatch(deleteSlideAction({ id }));
      return;
    }
    const withGroup = window.confirm(
      children.length === 1
        ? "This slide has 1 evidence slide — archive it too?"
        : `This slide has ${children.length} evidence slides — archive them too?`,
    );
    dispatch(deleteSlideAction({ id, withGroup }));
  };
  const restoreSlide = (id: string) => dispatch(restoreSlideAction({ id }));
  // §20 Permanent delete, from the archive only.
  //
  // §20.0.3 The order is the whole feature: drop each content window's artifacts FIRST,
  // while the slide is still in the document and its `griddDocId`s and `assetId`s are still
  // readable, THEN splice it out. After the splice those ids are unreachable from the deck
  // record and the grid body and image blob sit in IndexedDB forever with nothing pointing
  // at them. `dropContentArtifacts` is the same helper Clear Slide and the two→single
  // collapse already use (§20.0.2) — there is deliberately no second copy of it.
  //
  // §20.1.1 The hook §17 needs: once a nugget's body can be shared between decks, a slide's
  // `griddDocId` may belong to four other decks and dropping it here is silent cross-deck
  // data loss. The ownership gate goes in `dropContentArtifacts`, so that every caller gets
  // it at once — not here, or the next caller reintroduces the bug.
  const purgeSlide = (id: string) => {
    // §20.0.5 A viewer's writes go nowhere, so a purge would appear to work and come back on
    // reload — worse than refusing, because it destroys the belief that the archive is real.
    if (readOnly) {
      toast.error("This deck is open in another window", {
        description: "Take over editing here first.",
      });
      return;
    }
    const slide = slides.find((s) => s.id === id);
    if (!slide) return;
    // §20.0.4 `window.confirm`, matching the stylesheet-discard path rather than introducing
    // a dialog component for one call site. Named, because "are you sure" about an unnamed
    // slide in a list of archived ones is not a question anyone can answer.
    const name = slide.title.trim() || "this slide";
    if (
      !window.confirm(
        `Delete ${name} permanently? This cannot be undone — its grids and images are deleted with it.`,
      )
    ) {
      return;
    }
    for (const content of slide.contents) {
      dropContentArtifacts(dispatch, content ?? null, deckId);
    }
    dispatch(purgeSlideAction({ id }));
    toast.success("Slide deleted permanently");
  };
  const moveSlide = (id: string, direction: "up" | "down") =>
    dispatch(moveSlideAction({ id, direction }));

  // Save: promote the draft into the committed sheet (→ IndexedDB via the listener),
  // clear the draft, close. Cancel: drop the draft; embeds revert as the effective
  // sheet falls back to committed and re-pushes (TODO §10.7).
  const handleStyleSave = (sheet: GriddStyleSheetData) => {
    // §15/§6.3.2 **The chart-style round-trip guard.** Gridd's stylesheet editor rebuilds the
    // sheet from its own internal model, so any key it has never heard of can be dropped on
    // Save — and `chartStyles` (§6.2.1) is exactly such a key. Without this line, styling a
    // deck's charts and then changing one colour in that editor would silently delete every
    // chart style, with nothing anywhere saying so.
    //
    // §6.3.1 asks for that to be tested first; §6.3.2 asks for the merge **regardless of the
    // answer**, and that is what is done here. The test can only say what today's
    // `@gridd/embed` does, and the next release of it is not ours. `mergeChartStyles` carries
    // forward only the two keys §15 owns, per sheet by name — everything the editor did emit
    // wins outright, so this is a backstop for keys it cannot know about and never a general
    // "the old sheet is right" rule.
    dispatch(setStyleSheetAction(mergeChartStyles(committedStyleSheet, sheet)));
    dispatch(draftCommitted());
    setPanel(null);
  };
  const handleStyleCancel = () => {
    dispatch(draftAbandoned());
    setPanel(null);
  };
  // The panel's own close (X). Treated as Cancel, but warns first if a draft is dirty
  // so unsaved preview edits aren't silently dropped.
  const closePanel = () => {
    if (!leavingStylePanelIsOk()) return;
    setPanel(null);
  };
  // …and the same question, asked when switching to a SIBLING panel rather than closing. The
  // stylesheet editor is the only panel that holds unsaved state, and leaving it by any door
  // drops that state — so the door the in-header switcher opened has to ask too, or it
  // becomes the one way to lose a preview silently.
  const leavingStylePanelIsOk = () => {
    if (panel !== "style" || !styleDirty) return true;
    if (!window.confirm("Discard unsaved style changes?")) return false;
    dispatch(draftAbandoned());
    return true;
  };
  const switchPanel = (next: PanelId) => {
    if (!leavingStylePanelIsOk()) return;
    setPanel(next);
  };

  const active = slides.filter((s) => !s.archived);
  const archived = slides.filter((s) => s.archived);
  // §19.0.3/§18.2.9 One numbering pass for the deck, handed to every renderer. A cover draws
  // no number and consumes none, evidence is numbered under its parent and backups run their
  // own `B` series — so the editor, the nav, presentation, the PDF and the exported HTML all
  // read off this one array and cannot disagree.
  const numbers = slideNumbering(active);
  // §18.2.5 …and one grouping pass, feeding BOTH slide lists and the centre column: rows
  // already numbered, already indented, already knowing whether their arrows do anything,
  // with a Backups section row in front of the suffix.
  const outline = groupSlides(active, numbers);

  // §12.3.2 The deck card's preview on the documents home is the first active slide's
  // thumbnail — already produced by the offscreen capturer for the slide nav, so this costs
  // one small write when it changes and nothing otherwise. It goes on the `deckIndex` row,
  // never on the deck record, which is rewritten on every title keystroke and must stay
  // small. A viewer doesn't write it: the page that owns the deck owns its preview too.
  const coverThumb = active[0] ? slideThumbs[active[0].id]?.url : undefined;
  useEffect(() => {
    if (readOnly || !coverThumb) return;
    void putDeckThumb(deckId, coverThumb);
  }, [deckId, coverThumb, readOnly]);

  // Keep the scroll handler's view of the active slides current without re-binding it.
  useEffect(() => {
    activeIdsRef.current = active.map((s) => s.id);
  });

  // §8.0.4 Always keep a valid selection while slides exist: default to the first active
  // slide, and re-point when the selected one is deleted or archived. Falls back to null
  // only when there are genuinely no active slides (what the Library copy button keys off).
  useEffect(() => {
    const ids = slides.filter((s) => !s.archived).map((s) => s.id);
    if (ids.length === 0) {
      if (selectedSlideId !== null) setSelectedSlideId(null);
    } else if (!selectedSlideId || !ids.includes(selectedSlideId)) {
      setSelectedSlideId(ids[0]);
    }
  }, [slides, selectedSlideId]);

  // §8.0.2 (fallback) Closest-to-center selection while scrolling. Coalesced to one
  // measurement per animation frame, and suppressed for ~1s after any explicit interaction
  // so it doesn't fight the last-interacted value. Bound once — it reads the live active
  // ids and interaction timestamp through refs.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    let raf = 0;
    const measure = () => {
      raf = 0;
      if (Date.now() - lastInteractionRef.current < 1000) return;
      const box = container.getBoundingClientRect();
      const center = box.top + box.height / 2;
      let bestId: string | null = null;
      let bestDist = Infinity;
      for (const id of activeIdsRef.current) {
        const el = document.getElementById(`slide-${id}`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        const dist = Math.abs(r.top + r.height / 2 - center);
        if (dist < bestDist) {
          bestDist = dist;
          bestId = id;
        }
      }
      if (bestId) setSelectedSlideId(bestId);
    };
    const onScroll = () => {
      if (raf === 0) raf = requestAnimationFrame(measure);
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // The griddDocIds of every active slide's grid, in deck order — the set to snapshot before
  // entering an overlay that should render static images instead of live iframes.
  const activeGriddIds = () =>
    active.flatMap((s) =>
      s.contents.flatMap((c) => (c?.kind === "gridd" ? [c.griddDocId] : [])),
    );

  // Present (§5): like the PDF path, snapshot every active grid off its live, already-painted
  // editor embed BEFORE mounting the presentation overlay — so `PresentationView` renders
  // static `<img>`s instead of mounting fresh cross-origin iframes that visibly reload and
  // break the immersion (§5.5). Grids whose capture fails fall back to a live embed there.
  // Capture is async but fast (the editor iframes are already loaded), so it stays within the
  // click's transient activation and fullscreen still engages; the map is dropped on exit.
  const startPresenting = async () => {
    if (active.length === 0) return;
    const griddIds = activeGriddIds();
    setSnapshots(griddIds.length ? await requestSnapshots(griddIds) : {});
    setPresenting(true);
  };

  // Export → PDF (TODO §6.2 / §6.6.4): first snapshot every active grid off its live editor
  // embed (they're already mounted and painted), THEN enter print mode with those images in
  // hand — so the print layer renders `<img>`s, not iframes. Per-grid capture failures are
  // tolerated (those grids fall back to a live embed in print). No-op with nothing to print.
  const exportPdf = async () => {
    if (active.length === 0) return;
    const griddIds = activeGriddIds();
    setSnapshots(griddIds.length ? await requestSnapshots(griddIds) : {});
    setPrinting(true);
  };

  // §14.7.4 The desktop shell's last step, handed to `PrintView` in place of the print
  // dialog. Electron has no print preview, so `window.print()` would land the deck on the
  // system's portrait paper; main renders the same print layer with the page box set to the
  // slide's own canvas, and the bytes go through the usual save panel. Memoised because
  // `PrintView` latches it when the layer mounts.
  const capturePdf = useCallback(async () => {
    const shell = desktopShell();
    if (!shell) return;
    try {
      const pdf = await shell.printToPdf(SLIDE_W, SLIDE_H);
      await saveFile(pdf, exportFileName(deckTitle, "pdf"), "application/pdf");
    } catch (err) {
      // The dialog path announces its own failures by simply not appearing; this one is
      // silent unless we say something, and a deck that never reaches disk must not look
      // like a deck that was exported.
      console.warn("[export] desktop PDF export failed", err);
      toast.error("Could not export the PDF");
    }
  }, [deckTitle]);

  // Export → HTML: produce a SINGLE self-contained `.html` file with no runtime dependency
  // (offline, local-only scripting; §6.6 "the same snapshots unlock an HTML export"). Same
  // first step as the PDF path — snapshot every active grid off its live editor embed — then
  // bake image assets to data URLs and serialize the deck to static HTML, and hand the file
  // to the browser to download. Gated behind login in the TopBar like PDF (§9.4). No-op with
  // nothing to export.
  const exportHtml = async () => {
    if (active.length === 0) return;
    const griddIds = activeGriddIds();
    const snaps = griddIds.length ? await requestSnapshots(griddIds) : {};
    const images = await collectImageDataUrls(active);
    // §21/§11.2 Diagrams render from the STORE's bodies, not from the on-screen cache: the
    // export must not depend on a window having been scrolled into view. Fonts are inlined
    // here and only here (§5.4).
    const diagrams = await collectDiagramSvgs(active, diagramDocs);
    // §26/§9.2 Gantts bake to PNGs off-screen from the same store bodies, for the reason
    // written on `collectGanttImages`: the widget cannot travel into a single offline file.
    const gantts = await collectGanttImages(
      active,
      ganttDocs,
      committedStyleSheet,
    );
    // §15/§9.2 Charts serialize to real SVG off the same store bodies. §9.4: the CACHED rows
    // and nothing else — an export never fetches, so a deck exported on a train is the deck
    // that was last looked at rather than an empty frame.
    const charts = await collectChartSvgs(
      active,
      chartDocs,
      committedStyleSheet,
    );
    // §24.4.1 The logo is deck-level, so it is in no slide's contents and the image pass
    // above never sees it — one extra read, one extra data URL.
    const logo = await collectLogoDataUrl(deckLogo);
    const html = buildDeckHtml({
      title: deckTitle,
      slides: active,
      styleSheet: committedStyleSheet,
      transition,
      snapshots: snaps,
      images,
      diagrams,
      gantts,
      charts,
      logo,
    });
    downloadHtml(html, exportFileName(deckTitle, "html"));
  };

  // §12.6.1 Export → deck file. The one export that is a *backup* rather than a published
  // artefact, and the only one a browser-only user has; it reads straight from IndexedDB, so
  // it needs no snapshots and works identically from a read-only page.
  const exportDeckArchive = async () => {
    if (!(await exportDeckFile(deckId))) {
      toast.error("This deck is no longer in storage");
    }
  };

  // === Knowledge nuggets (TODO §17) ===

  // The shelf's listing rows, indexed by id — only so the slide badge's menu can say which
  // nugget a slide is linked to and when it last changed (§17.4.2). Refreshed on the same two
  // channel messages the panel watches, plus whenever a projection bumps a rev in this page.
  const [nuggetIndex, setNuggetIndex] = useState<
    Record<string, NuggetIndexEntry>
  >({});
  const refreshNuggetIndex = useCallback(() => {
    void listNuggets().then((rows) =>
      setNuggetIndex(Object.fromEntries(rows.map((r) => [r.nuggetId, r]))),
    );
  }, []);
  useEffect(refreshNuggetIndex, [refreshNuggetIndex]);

  // §17.2.4 A nugget changed in ANOTHER window — another deck being edited, or this same
  // nugget on a different page. Every slide here that links it re-materializes live, which is
  // what makes two windows on two decks look like one document.
  //
  // A read-only viewer is deliberately included: it is not writing anything (the persistence
  // listener drops the resulting deck write), and showing it a stale copy of a slide it can
  // see being edited elsewhere would be the exact confusion §12 exists to end.
  useEffect(() => {
    return subscribeDeckMessages((msg) => {
      if (msg.type === "nugget-deleted") {
        // Deliberately NOT dropping the link here. The slide keeps everything it has and the
        // link falls away on the next open (§17.2.5/§17.6.1) — a nugget deleted on the shelf
        // must never change what is on screen in a deck somebody is working in.
        refreshNuggetIndex();
        return;
      }
      if (msg.type !== "nugget-updated") return;
      refreshNuggetIndex();
      void getNugget(msg.nuggetId).then((record) => {
        if (!record) return;
        dispatch(
          nuggetApplied({
            nuggetId: record.nuggetId,
            rev: record.rev,
            fields: materializeFrom(record),
          }),
        );
      });
    });
  }, [dispatch, refreshNuggetIndex]);

  // §17.4.5 The tile images, taken off the deck's own slide thumbnails rather than by a
  // second offscreen capturer — `putDeckThumb`'s trick, applied to the shelf. One small write
  // when a linked slide's thumbnail changes, and nothing otherwise. A viewer writes none: the
  // page that owns the deck owns the previews it produces.
  const linkedThumbs = active.flatMap((s) =>
    s.link && slideThumbs[s.id]
      ? [{ nuggetId: s.link.nuggetId, url: slideThumbs[s.id].url }]
      : [],
  );
  // The effect keys off a signature and reads the live array through a ref, so a re-render
  // that produces an equal list writes nothing — the same shape `coverThumb` above uses,
  // widened to several rows.
  const linkedThumbsKey = linkedThumbs
    .map((t) => `${t.nuggetId}\u0000${t.url}`)
    .join("\u0001");
  const linkedThumbsRef = useRef(linkedThumbs);
  linkedThumbsRef.current = linkedThumbs;
  useEffect(() => {
    if (readOnly) return;
    for (const t of linkedThumbsRef.current) {
      void putNuggetThumb(t.nuggetId, t.url);
    }
  }, [linkedThumbsKey, readOnly]);

  // A guard every nugget action starts with. Three of the four write to a deck AND to the
  // shelf, and a viewer's deck write goes nowhere (§12.5.1) — so the shelf would end up
  // holding a nugget no deck links, which is worse than refusing.
  const refuseIfReadOnly = () => {
    if (!readOnly) return false;
    toast.error("This deck is open in another window", {
      description: "Take over editing here first.",
    });
    return true;
  };

  // §17.4.4 Save a slide to the shelf. The slide becomes the first user of the nugget it just
  // created, which is what anyone would expect — and it is also what makes the round trip
  // testable in one click: the badge appears on the slide you saved.
  const saveAsNugget = async (slideId: string) => {
    if (refuseIfReadOnly()) return;
    const slide = slides.find((s) => s.id === slideId);
    if (!slide) return;
    // §8.4.3's rule, for the same reason: grid writes are suppressed mid stylesheet-preview,
    // so the bodies this would copy are whatever survived the suppression rather than what is
    // on screen. Refuse rather than save a nugget nobody can explain.
    if (styleDirty) {
      toast.error("Finish editing the style sheet before saving a nugget.");
      return;
    }
    const record = await saveSlideAsNugget(dispatch, slide, {
      gridd: griddDocs,
      diagram: diagramDocs,
      gantt: ganttDocs,
      chart: chartDocs,
    });
    if (!record) {
      toast.error("Could not save this slide to the shelf");
      return;
    }
    refreshNuggetIndex();
    toast.success(`Saved “${slide.title.trim() || "Untitled"}” to the shelf`, {
      description: "Edits to this slide now reach every deck that uses it.",
    });
  };

  // §17.4.3 Break link. The confirm states the one-way rule plainly, because it IS one-way:
  // a broken-off slide is never merged back.
  const breakLink = async (slideId: string, thenClear = false) => {
    if (refuseIfReadOnly()) return;
    const slide = slides.find((s) => s.id === slideId);
    if (!slide?.link) return;
    // §17.6.3 came in through Clear Slide, which has already asked its own — and better —
    // question. Asking twice for one click is how a confirm stops being read.
    if (
      !thenClear &&
      !window.confirm(
        "Break the link to this nugget? From here on this slide is this deck's own, and nothing you do to it comes back to the nugget.",
      )
    ) {
      return;
    }
    await breakSlideLink(
      dispatch,
      slide,
      {
        gridd: griddDocs,
        diagram: diagramDocs,
        gantt: ganttDocs,
        chart: chartDocs,
      },
      deckId,
    );
    if (!thenClear) {
      toast.success("Link broken — this slide is now this deck's own");
      return;
    }
    // §17.6.3 The clear half, run HERE rather than in `Slide` because it must operate on the
    // contents the break just wrote — the fresh, deck-owned copies. `Slide` holds the slide
    // as a prop and would still be looking at the nugget's ids, so its cleanup would gate
    // itself out and leave the copies it just made orphaned.
    const broken = store
      .getState()
      .document.slides.find((s) => s.id === slideId);
    if (!broken) return;
    for (const content of broken.contents) {
      dropContentArtifacts(dispatch, content ?? null, deckId);
    }
    updateSlide(slideId, {
      title: "",
      source: "",
      contents: broken.contents.map(() => null) as SlideContents,
    });
    toast.success("Slide cleared — it is no longer linked to a nugget");
  };

  // §17.4.5 A picked nugget lands as a NEW slide appended to the deck — the Library's rule
  // (§8.4.2), and for the same reason: the selected slide is wherever the scroll position
  // landed, and overwriting it is a destructive act triggered by a browse.
  const insertNugget = async (nuggetId: string) => {
    if (refuseIfReadOnly()) return;
    // §17.2.6 At most once per deck in v1. Two slides in one open deck linked to one nugget
    // means two live embeds mounted on the same `griddDocId`, racing each other's ~400ms
    // saves into a body four other decks are reading. Named, because "already in this deck"
    // is only useful if the user can find it.
    const existing = slideUsingNugget(slides, nuggetId);
    if (existing) {
      const at = active.findIndex((s) => s.id === existing.id);
      toast.error("This deck already uses that nugget", {
        description:
          at >= 0 && numbers[at]
            ? `It is on slide ${numbers[at]}.`
            : "It is already on a slide in this deck.",
      });
      selectSlide(existing.id);
      setTimeout(() => scrollTo(existing.id), 50);
      return;
    }
    const added = dispatch(addSlideAction());
    const newId = added.payload.id;
    const record = await materializeNuggetIntoSlide(dispatch, newId, nuggetId);
    if (!record) {
      toast.error("That nugget is no longer on the shelf");
      return;
    }
    selectSlide(newId);
    setTimeout(() => scrollTo(newId), 50);
    toast.success(
      `Added “${record.slide.title.trim() || "Untitled nugget"}” as a new slide`,
    );
  };

  // §8.4.2 A picked library slide lands as a NEW slide appended to the deck — it never
  // overwrites the selected slide. The payload is adapted to a valid, collision-free
  // GriddDocument (§8.5) and stored under a fresh docId; a fresh single-window slide is
  // added and pointed at it, then selected and scrolled into view. Persistence is automatic
  // via griddDocCreated + updateSlide (§8.4.3).
  const copyLibrarySlide = (input: GriddSlideDocument, pick: LibraryPick) => {
    // §12.5.2 A viewer's writes go nowhere, so adding a slide here would look like it worked
    // and vanish on reload. Refuse, and say why.
    if (readOnly) {
      toast.error("This deck is open in another window", {
        description: "Take over editing here first.",
      });
      return;
    }
    // §8.4.3 Grid writes are suppressed mid stylesheet-preview, so refuse rather than drop it.
    if (styleDirty) {
      toast.error("Finish editing the style sheet before adding a slide.");
      return;
    }
    const docId = crypto.randomUUID();
    dispatch(griddDocCreated({ docId, doc: libraryDocToGriddDocument(input) }));
    const added = dispatch(addSlideAction());
    const newId = added.payload.id;
    updateSlide(newId, { contents: [{ kind: "gridd", griddDocId: docId }] });
    selectSlide(newId);
    // Let the new slide mount before bringing it into view.
    setTimeout(() => scrollTo(newId), 50);
    toast.success(`Added “${pick.name ?? "slide"}” as a new slide`);
  };

  const scrollTo = (id: string) => {
    const container = scrollRef.current;
    const el = document.getElementById(`slide-${id}`);
    if (!container || !el) return;
    const top =
      el.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop;
    container.scrollTo({ top, behavior: "smooth" });
  };

  const displayTitle = (s: SlideData, indexInActive: number) =>
    s.title.trim() ||
    (s.archived
      ? "Untitled slide"
      : numbers[indexInActive] === null
        ? "Cover"
        : `Slide ${numbers[indexInActive]}`);

  // The end-of-deck add row. It belongs to the DECK, not to the column, so once a backup
  // section exists it is rendered in front of the Backups divider rather than at the bottom
  // of the page: "Add slide" appends a storyline slide, and `normalizePyramid` lands that in
  // front of the backups whatever the button's position — so a button sitting under the
  // appendix would put the new slide somewhere the user is not looking. The backup suffix
  // has its own button at the true end.
  const addSlideRow = !readOnly && (
    <div className="flex items-center gap-3">
      <Button onClick={addSlide} size="lg">
        <Plus className="mr-2 h-4 w-4" /> Add slide
      </Button>
      {/* §19.1.2 Offered only while the deck has no cover — a second one would be a title
          page in the middle of a deck, and the first is the one the numbering skips. */}
      {active[0]?.layout !== "cover" && (
        <Button onClick={addCover} size="lg" variant="outline">
          <Heading1 className="mr-2 h-4 w-4" /> Add cover
        </Button>
      )}
      {/* §18.2.8 Offered only while the deck has no backup section: a second *Backups*
          divider inside the suffix would divide nothing. Once one exists, the suffix's own
          "Add backup" button below takes over. */}
      {outline.backupCount === 0 && (
        <Button onClick={addBackupSection} size="lg" variant="outline">
          <Paperclip className="mr-2 h-4 w-4" /> Add backup section
        </Button>
      )}
    </div>
  );

  // The scrolling deck column. Always the editable editor now — fullscreen presentation is
  // a separate overlay (below), so this no longer branches on `presenting`.
  const centerColumn = (
    <div
      ref={scrollRef}
      className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto"
    >
      <main className="flex-1 px-6 py-8">
        <div
          className="mx-auto flex flex-col items-center gap-10"
          style={{ maxWidth: slideMaxWidth }}
        >
          {/* §18.2.6 The centre column carries the same grouping, lightly: an evidence slide
              indents and takes a thin left rule tying it to its parent, and a full-width
              divider precedes the backup section. All of it is CHROME — the wrappers sit
              outside `.slide-canvas`, for the reason §17.4.1 gives: anything inside the
              canvas is exported, and a deck's PDF must not grow an indent. */}
          {outline.rows.map((row) => {
            if (row.kind === "section") {
              return (
                <Fragment key={row.id}>
                  {/* The deck ends here, so its add row does too — the backup section is
                      behind the deck, not the end of it. */}
                  {addSlideRow}
                  <div
                    className="flex w-full items-center gap-4"
                    style={{ maxWidth: slideMaxWidth }}
                  >
                    <span className="h-px flex-1 bg-slate-300" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      {row.label}
                    </span>
                    <span className="h-px flex-1 bg-slate-300" />
                  </div>
                </Fragment>
              );
            }
            const s = row.slide;
            const i = row.index;
            return (
              <div key={s.id} className="w-full" style={{ maxWidth: SLIDE_W }}>
                <div
                  className={cn(
                    row.indent > 0 &&
                      "border-l-2 border-slate-200 pl-6 md:pl-10",
                  )}
                >
                  <Slide
                    slide={s}
                    index={i}
                    locked={readOnly}
                    onChange={(patch) => updateSlide(s.id, patch)}
                    onDelete={readOnly ? undefined : () => deleteSlide(s.id)}
                    onMoveUp={
                      readOnly ? undefined : () => moveSlide(s.id, "up")
                    }
                    onMoveDown={
                      readOnly ? undefined : () => moveSlide(s.id, "down")
                    }
                    // §18.2.3 Straight from the grouping pass, so the arrows are disabled by
                    // exactly the rules `movePyramid` refuses by — a backup slide at the top
                    // of the suffix, the last group's last evidence slide, and so on.
                    canMoveUp={row.canMoveUp}
                    canMoveDown={row.canMoveDown}
                    onInsertAfter={
                      readOnly ? undefined : () => addSlideAfter(s.id)
                    }
                    onAddEvidence={
                      readOnly || row.level === "backup" || s.layout === "cover"
                        ? undefined
                        : () => addSlideAfter(s.id, "evidence")
                    }
                    onSetLevel={
                      readOnly
                        ? undefined
                        : (level) => setSlideLevel(s.id, level)
                    }
                    levelLocked={i === 0}
                    // §17.4.4/§17.4.3 The two nugget actions, both refused on a read-only
                    // page by the handlers themselves (which say why, rather than the button
                    // simply not being there — a viewer needs to know the slide IS shared).
                    onSaveAsNugget={
                      readOnly ? undefined : () => void saveAsNugget(s.id)
                    }
                    onBreakLink={
                      readOnly
                        ? undefined
                        : (opts) =>
                            void breakLink(s.id, opts?.thenClear ?? false)
                    }
                    // §17.4.6 The nugget's name IS the slide's title, so falling back to the
                    // slide's own is not a guess — it is the same value, read from the copy
                    // in hand when the shelf's listing row has not caught up with a rename
                    // this page just made (BroadcastChannel never delivers a page its own
                    // posts, which is exactly what keeps the projection from looping).
                    nuggetName={
                      s.link
                        ? (nuggetIndex[s.link.nuggetId]?.name ?? s.title)
                        : undefined
                    }
                    nuggetUpdatedAt={
                      s.link
                        ? nuggetIndex[s.link.nuggetId]?.updatedAt
                        : undefined
                    }
                    pageNumber={row.number}
                    onSelect={() => selectSlide(s.id)}
                  />
                </div>
              </div>
            );
          })}
          {/* The deck's own add row sits ABOVE the Backups divider (see `addSlideRow`);
              when there is no backup section it is simply the last thing in the column. */}
          {outline.backupCount === 0 && addSlideRow}
          {/* …and the suffix gets its own, after the last backup slide. */}
          {outline.backupCount > 0 && !readOnly && (
            <div className="flex items-center gap-3">
              <Button onClick={addBackupSlide} size="lg" variant="outline">
                <Paperclip className="mr-2 h-4 w-4" /> Add backup
              </Button>
            </div>
          )}

          {archived.length > 0 && (
            <Collapsible
              open={archiveOpen}
              onOpenChange={setArchiveOpen}
              className="w-full"
              style={{ maxWidth: slideMaxWidth }}
            >
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  <span>Archive ({archived.length})</span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${archiveOpen ? "rotate-180" : ""}`}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-6 flex flex-col items-center gap-10">
                {archived.map((s, i) => (
                  <Slide
                    key={s.id}
                    slide={s}
                    index={i}
                    locked={readOnly}
                    onChange={(patch) => updateSlide(s.id, patch)}
                    onRestore={readOnly ? undefined : () => restoreSlide(s.id)}
                    onPurge={readOnly ? undefined : () => purgeSlide(s.id)}
                  />
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </main>

      {/* Website chrome only (§14.7.1): the shell has a native menu bar carrying these links
          and the copyright, so a footer under every deck would be the second copy. */}
      {isElectron() ? null : <Footer />}
    </div>
  );

  // The right-panel body (header + live editor / template image), reused inside the
  // resizable "side" panel. It is user-resizable now, so the old fixed 380/520 widths
  // are gone — constraints live on the ResizablePanel below.
  const panelSwitcher = !readOnly && (
    <PanelSwitcher panel={panel} onPick={switchPanel} />
  );

  const sidePanel = (
    <aside className="flex h-full min-w-0 flex-col border-l border-slate-200 bg-white">
      {/* The assistant draws its own header: it needs the model picker and the clear/settings
          controls next to the title, so the shared header row below would only be a second,
          emptier copy of it. Everything else — the border, the width, the close behaviour,
          and now the switcher — is still the pane's. */}
      {panel === "assistant" ? (
        <AiTerminal onClose={closePanel} switcher={panelSwitcher} />
      ) : (
        <>
          {/* Icons only — the switcher IS the header (see `panelSwitcher`). Keeping every
              panel in reach while one is open is the whole point of the row: the rail below
              is hidden whenever a panel is open, so this is the only way across. */}
          <div className="flex items-center gap-1 border-b border-slate-200 px-2 py-2">
            {panelSwitcher}
            <div className="min-w-0 flex-1" />
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={closePanel}
              aria-label="Close panel"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="min-h-0 flex-1">
            {panel === "style" ? (
              <GriddStyleSheetEditor
                baseUrl={GRIDD_EMBED_BASE_URL}
                styleSheet={editableStyleSheet}
                variant="panel"
                width="100%"
                height="100%"
                // Draft/save/cancel cross from the embed's loose type back to ours.
                onDraftChange={(sheet) =>
                  dispatch(
                    // §6.3.2 The same merge on the DRAFT, and it is not redundant: the
                    // effective sheet during a preview IS the draft (§10.1), so a draft
                    // missing `chartStyles` would make every chart in the deck jump to its
                    // defaults the moment the stylesheet panel was touched — and jump back on
                    // Cancel. The preview has to show the deck's real look.
                    draftChanged(
                      mergeChartStyles(
                        committedStyleSheet,
                        sheet as unknown as GriddStyleSheetData,
                      ),
                    ),
                  )
                }
                onSave={(sheet) =>
                  handleStyleSave(sheet as unknown as GriddStyleSheetData)
                }
                onCancel={handleStyleCancel}
              />
            ) : panel === "library" ? (
              // §8.3/§8.4.1 The embeddable Gridd Library. The bridge owns the whole postMessage
              // protocol (origin guards, the token-freshness handshake); the host's only job is
              // to feed it the studio session (§8.3):
              //   • idToken / getIdToken — the signed-in user's Firebase ID token, forwarded to
              //     the premium server as a bearer (§8.3.1). Passing both gives an eager push on
              //     identity change plus on-demand refresh of an expired token (§8.3.3). The
              //     server is the trust boundary (§8.3.2) — lock badges here are UX only.
              //   • onSignInRequest — a premium slide while signed out routes to the studio's
              //     sign-in (§8.3.5); there is no second login in the panel.
              // Cross-origin token handling (§8.3.4: post only to the validated `black-ground`
              // origin, never "*") lives inside the bridge, keyed off `baseUrl`.
              // §14.6.5/§14.8.4 Offline the panel degrades, it is never hidden: the catalog still
              // browses from the package's cache (§14.2), and the rail button stays where it was.
              // What changes is that we say why premium slides may not open — a non-blocking line,
              // because a feature that quietly stops working mid-flight is worse than one with a
              // stated limit.
              <div className="flex h-full min-h-0 flex-col">
                {authStatus === "in" && !tokenFresh && !online && (
                  <p className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    {withinOfflineGrace
                      ? "Working offline — slides already saved on this device still open. Reconnect once to refresh the library."
                      : "Working offline for a while now — reconnect once to open Gridd Library slides again. Your own slides are unaffected."}
                  </p>
                )}
                <div className="min-h-0 flex-1">
                  <GriddLibrary
                    baseUrl={GRIDD_LIBRARY_BASE_URL}
                    styleSheet={
                      committedStyleSheet as unknown as LibraryStyleSheetData
                    }
                    idToken={idToken}
                    getIdToken={({ forceRefresh }) => getIdToken(forceRefresh)}
                    width="100%"
                    height="100%"
                    onSignInRequest={promptSignIn}
                    onSlidePicked={copyLibrarySlide}
                    onError={(err) => toast.error(err.message)}
                  />
                </div>
              </div>
            ) : panel === "transition" ? (
              // §10.3 Deck-level slide-transition picker (drives presentation mode, §5/§10).
              <TransitionPanel />
            ) : panel === "diagram" ? (
              // §21/§8 The attribute editor for whatever is selected in the diagram being
              // edited, plus the diagram-level group (canvas background, grid, snapping,
              // the page frame, "Match deck colours").
              <DiagramPanel />
            ) : panel === "gantt" ? (
              // §26/§8 SVAR's own task editor for the live chart, plus our view controls.
              <GanttPanel />
            ) : panel === "chart" ? (
              // §15/§7 The selected chart's own settings and its data source, plus the deck's
              // shared chart style — the second group written into `document.styleSheet`, so
              // it travels with the deck and restyles every chart at once (§6).
              <ChartPanel />
            ) : panel === "nuggets" ? (
              // §17.4.5 The local nugget shelf. A pick APPENDS a new slide (§8.4.2's rule),
              // and the panel itself knows nothing about how a slide is added.
              <NuggetPanel
                onInsert={(nuggetId) => void insertNugget(nuggetId)}
                readOnly={readOnly}
              />
            ) : panel === "logo" ? (
              // §24.3 Deck-level logo: one image, one corner, every slide.
              <LogoPanel />
            ) : (
              <div className="h-full overflow-y-auto">
                <img
                  src={templateLibrary}
                  alt="Template library"
                  className="h-auto w-full object-contain"
                />
              </div>
            )}
          </div>
        </>
      )}
    </aside>
  );

  return (
    <div className="app-screen flex flex-col bg-slate-100">
      {/* §14.7.1 Two different chromes for two different runtimes. On the web the TopBar is
          the app's own title bar — logo, deck switcher, export, present, account. In the
          shell the window already HAS a title bar and a native menu, so a full-width header
          under them is a third row saying the same things: the tab strip carries the document
          name (rename it in place) and the three actions as icons instead. */}
      {isElectron() ? (
        <DesktopTabs
          onPresent={startPresenting}
          onExportPdf={exportPdf}
          onExportHtml={exportHtml}
          onExportDeckFile={() => void exportDeckArchive()}
          readOnly={readOnly}
        />
      ) : (
        <TopBar
          onPresent={startPresenting}
          onExportPdf={exportPdf}
          onExportHtml={exportHtml}
          onExportDeckFile={() => void exportDeckArchive()}
          readOnly={readOnly}
        />
      )}
      {/* §12.5.2 The honest banner. A second page on the same deck opens read-only rather
          than racing the first one's writes, and "Edit here" asks the holder to stand down.
          Live multi-writer editing needs a CRDT and is out of scope (§12.5.2). */}
      {readOnly && (
        <div className="flex flex-wrap items-center justify-center gap-3 border-b border-amber-200 bg-amber-50 px-6 py-2 text-sm text-amber-900">
          <Eye className="h-4 w-4 shrink-0" />
          <span>
            This deck is open in another window. You can look around, but
            changes made here won’t be saved.
          </span>
          {onTakeOver && (
            <Button
              variant="outline"
              size="sm"
              onClick={onTakeOver}
              disabled={takingOver}
            >
              {takingOver ? "Taking over…" : "Edit here"}
            </Button>
          )}
        </div>
      )}
      {/* The register provider scopes to the EDITOR body only: every editor <GriddEmbed>
          announces its snapshot handle here (§6.6.4), and `exportPdf` pulls from them. It
          must NOT wrap the presentation/print overlays below — their embeds share the same
          griddDocId and would clobber (then, on close, deregister) the editor's handle. */}
      <SnapshotRegisterProvider register={register}>
        <ThumbnailScheduleProvider schedule={scheduleThumbnail}>
          {/* §21/§5.1 THE ONE-INSTANCE BOUNDARY. Everything inside may mount the live
              Excalidraw editor — at most one, because the provider holds at most one
              `editingDocId`. The presentation overlay, the print layer and the off-screen
              thumbnail capturer are all rendered OUTSIDE it (below, next to the providers
              they also sit outside), so their copies of `<Slide>` get the inert default and
              render the cached SVG instead. The guarantee is structural, not a convention.
              §6.3/§7.1: entering edit switches the left panel to Elements and opens the
              Diagram panel; leaving restores Slides. */}
          <DiagramEditingProvider
            onEnterEdit={() => {
              setNavTab("elements");
              // Through `switchPanel`, not `setPanel`: double-clicking a diagram while the
              // stylesheet editor holds an unsaved draft would otherwise drop that draft with
              // no question asked, which is the one thing every other door out of that panel
              // is careful not to do.
              switchPanel("diagram");
            }}
            onExitEdit={() => setNavTab("slides")}
          >
            {/* §26/§8 The gantt api boundary, inside the diagram one for the same structural
                reason: only the editor body may publish a live api, so the presentation,
                print and off-screen copies of `<Slide>` below cannot — they render their
                gantts read-only and reach the inert default. */}
            <GanttEditingProvider
              // §26/§8.5 A double click inside a gantt is the widget asking to show its task
              // editor — and ours is in this panel, so the panel has to come up. Without it
              // the gesture moved the selection inside a pane that was not on screen and read
              // as "nothing happened". The diagram's `onEnterEdit` above is the same wire for
              // the same gesture, and this goes through the same confirming door.
              onEnterEdit={() => switchPanel("gantt")}
            >
              {/* §15/§7.2 The chart selection boundary, inside the other two for the same
                  structural reason — and it carries one extra job the others do not: its
                  `isEditor` flag is what tells a `ChartBlock` it is allowed to start a refresh
                  timer at all (§5.3.4). The presentation overlay, the print layer and the
                  off-screen thumbnail capturer render `<Slide>` OUTSIDE this, so their charts
                  reach the inert default and can never go to the network. */}
              <ChartEditingProvider>
                <div className="flex flex-1 min-h-0">
                  <ResizablePanelGroup
                    orientation="horizontal"
                    defaultLayout={defaultLayout}
                    onLayoutChanged={onLayoutChanged}
                    className="min-h-0 flex-1"
                  >
                    {/* Left slide-nav. Constrained so it can't crowd out the deck (§1.3). */}
                    <ResizablePanel
                      id="nav"
                      defaultSize={240}
                      minSize={160}
                      maxSize={360}
                      className="min-w-0 border-r border-slate-200 bg-white"
                    >
                      <aside className="flex h-full flex-col overflow-hidden">
                        {/* Header. §21/§7.1 added the Slides|Elements tab row above the existing
                      two-way view toggle (thumbnails vs. a numbered title outline, persisted
                      to localStorage) — the toggle belongs to the Slides tab and goes away
                      with it. */}
                        <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
                          <ToggleGroup
                            type="single"
                            value={navTab}
                            onValueChange={(v) =>
                              v && setNavTab(v as "slides" | "elements")
                            }
                            variant="outline"
                            size="sm"
                            aria-label="Left panel"
                          >
                            <ToggleGroupItem
                              value="slides"
                              className="px-2 text-xs"
                            >
                              Slides
                            </ToggleGroupItem>
                            <ToggleGroupItem
                              value="elements"
                              className="px-2 text-xs"
                            >
                              Elements
                            </ToggleGroupItem>
                          </ToggleGroup>
                          {navTab === "slides" && (
                            <ToggleGroup
                              type="single"
                              value={navMode}
                              onValueChange={(v) =>
                                v && setNavMode(v as "thumbnail" | "outline")
                              }
                              variant="outline"
                              size="sm"
                              aria-label="Slide list view"
                            >
                              <ToggleGroupItem
                                value="thumbnail"
                                aria-label="Thumbnail view"
                                title="Thumbnails"
                              >
                                <Images className="h-4 w-4" />
                              </ToggleGroupItem>
                              <ToggleGroupItem
                                value="outline"
                                aria-label="Outline view"
                                title="Outline"
                              >
                                <List className="h-4 w-4" />
                              </ToggleGroupItem>
                            </ToggleGroup>
                          )}
                        </div>
                        {/* §21/§7.1 The Elements palette. Rendered BESIDE the slide list rather than
                      instead of it — see the `hidden` below — so switching tabs cannot lose
                      the list's scroll position or the selected slide. */}
                        {navTab === "elements" && <DiagramPalette />}
                        <TooltipProvider delayDuration={300}>
                          <nav
                            // §7.1 `hidden` rather than an unmount: the Elements tab is a MODE, and
                            // a mode that resets the slide list's scroll position and re-mounts a
                            // dozen thumbnails every time the author drops a shape is not one.
                            hidden={navTab !== "slides"}
                            className={cn(
                              // min-h-0 + flex-1 bounds the list inside the aside so it scrolls
                              // (rather than the aside clipping it) once the slides overflow.
                              "flex min-h-0 flex-1 flex-col overflow-y-auto",
                              navMode === "thumbnail" ? "gap-2 p-2" : "py-2",
                            )}
                          >
                            {/* §18.2.12 The editorial nudge, deck-level half: a quiet line,
                              never a block. A deck that needs twelve storyline slides is
                              allowed to have twelve. */}
                            {outline.storylineCrowded && (
                              <div
                                className={cn(
                                  "text-xs text-amber-700",
                                  navMode === "thumbnail"
                                    ? "px-1 pb-1"
                                    : "px-3 pb-2",
                                )}
                              >
                                {outline.storylineCount} storyline slides — the
                                principle suggests under {STORYLINE_SOFT_MAX}.
                              </div>
                            )}
                            {/* §18.2.5 One rendering pass over the SAME grouped rows the centre
                              column uses, in both modes. Selection and scroll behaviour are
                              untouched: this changed what a row looks like, not what it does. */}
                            {outline.rows.map((row) => {
                              if (row.kind === "section") {
                                // Sticky in the outline, a plain divider between tiles — the
                                // backup section can be long, and losing its header behind a
                                // scroll is how a backup slide gets mistaken for the deck.
                                return navMode === "thumbnail" ? (
                                  <div
                                    key={row.id}
                                    className="flex shrink-0 items-center gap-2 pt-2"
                                  >
                                    <span className="h-px flex-1 bg-slate-300" />
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                      {row.label}
                                    </span>
                                    <span className="h-px flex-1 bg-slate-300" />
                                  </div>
                                ) : (
                                  <div
                                    key={row.id}
                                    className="sticky top-0 z-10 mt-3 border-y border-slate-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-400"
                                  >
                                    {row.label}
                                  </div>
                                );
                              }
                              const s = row.slide;
                              const i = row.index;
                              const selected = s.id === selectedSlideId;
                              const onPick = () => {
                                selectSlide(s.id);
                                scrollTo(s.id);
                              };
                              // 1) Thumbnail only — full-slide image, no number or title text.
                              // An evidence tile is inset, which makes it slightly smaller in
                              // the same stroke: one padding rule, both effects.
                              if (navMode === "thumbnail") {
                                const thumb = slideThumbs[s.id]?.url;
                                return (
                                  <div
                                    key={s.id}
                                    className={cn(
                                      "shrink-0",
                                      row.indent > 0 && "pl-5",
                                    )}
                                  >
                                    <button
                                      onClick={onPick}
                                      aria-label={displayTitle(s, i)}
                                      className={cn(
                                        // shrink-0 keeps each thumbnail at its full aspect-ratio height
                                        // (which scales with the panel width) instead of the flex column
                                        // squashing them — so overflow scrolls rather than distorts.
                                        "block w-full shrink-0 overflow-hidden rounded-md border-2 transition-colors",
                                        selected
                                          ? "border-slate-900"
                                          : "border-transparent hover:border-slate-300",
                                      )}
                                    >
                                      <div className="relative aspect-video w-full bg-slate-100">
                                        {thumb && (
                                          <img
                                            src={thumb}
                                            alt=""
                                            className="h-full w-full object-contain"
                                          />
                                        )}
                                        {/* §17.4.2 A small link glyph on the nav thumbnail, so
                                          a long deck shows at a glance which slides are
                                          shared. On the OVERLAY rather than in the image: the
                                          thumbnail is a render of the slide as it exports,
                                          and nothing about the link belongs in that. */}
                                        {s.link && (
                                          <span
                                            title="Knowledge nugget"
                                            className="absolute right-1 top-1 rounded bg-white/90 p-0.5 text-sky-700 shadow-sm"
                                          >
                                            <Link2 className="h-3 w-3" />
                                          </span>
                                        )}
                                      </div>
                                    </button>
                                  </div>
                                );
                              }
                              // 2) Outline only — numbered title; full text in a hover tooltip.
                              // The number is the pyramid's (`4`, `4.2`, `B7`), so the outline
                              // reads as the argument rather than as a list of pages.
                              return (
                                <Tooltip key={s.id}>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={onPick}
                                      className={cn(
                                        "flex items-center gap-2 py-1.5 pr-3 text-left text-sm hover:bg-slate-100",
                                        // §8.0.3 Exactly one active item is bold.
                                        selected
                                          ? "font-semibold text-slate-900"
                                          : "text-slate-700",
                                      )}
                                      style={{
                                        paddingLeft: 12 + row.indent * 16,
                                      }}
                                    >
                                      <span className="w-8 shrink-0 tabular-nums text-xs text-slate-400">
                                        {row.number ?? "—"}
                                      </span>
                                      <span className="min-w-0 truncate">
                                        {displayTitle(s, i)}
                                      </span>
                                      {/* §17.4.2 The outline's half of the same glyph. */}
                                      {s.link && (
                                        <Link2
                                          aria-hidden
                                          className="h-3 w-3 shrink-0 text-sky-700"
                                        />
                                      )}
                                      {/* §18.2.12 The per-slide half of the nudge. */}
                                      {row.crowded && (
                                        <span
                                          aria-hidden
                                          className="ml-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400"
                                        />
                                      )}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="right">
                                    {displayTitle(s, i)}
                                    {row.crowded &&
                                      ` — more than ${EVIDENCE_SOFT_MAX} evidence slides`}
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })}
                            {archived.length > 0 && (
                              <>
                                <div className="mt-3 border-t border-slate-200 px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                                  Archive
                                </div>
                                {archived.map((s, i) => (
                                  <button
                                    key={s.id}
                                    onClick={() => {
                                      setArchiveOpen(true);
                                      setTimeout(() => scrollTo(s.id), 50);
                                    }}
                                    className="flex items-center gap-3 px-4 py-2 text-left text-sm text-slate-500 hover:bg-slate-100"
                                  >
                                    <span className="w-5 shrink-0 text-xs text-slate-400">
                                      {i + 1}.
                                    </span>
                                    <span className="truncate line-through">
                                      {s.title.trim() || "Untitled slide"}
                                    </span>
                                  </button>
                                ))}
                              </>
                            )}
                          </nav>
                        </TooltipProvider>
                      </aside>
                    </ResizablePanel>

                    <ResizableHandle withHandle />

                    {/* Center deck. minSize keeps it from ever collapsing to nothing (§1.3). */}
                    <ResizablePanel id="main" minSize={480} className="min-w-0">
                      {centerColumn}
                    </ResizablePanel>

                    {/* Right panel is a resizable member of the group only while open. The
                  48px icon rail below is a fixed toolbar, not a resizable region. */}
                    {panel !== null && (
                      <>
                        <ResizableHandle withHandle />
                        <ResizablePanel
                          id="side"
                          defaultSize={460}
                          minSize={320}
                          maxSize={760}
                          className="min-w-0"
                        >
                          {sidePanel}
                        </ResizablePanel>
                      </>
                    )}
                  </ResizablePanelGroup>

                  {/* The 48px icon rail, shown only while NO panel is open — an open panel
                    carries the same set in its own header (`panelSwitcher`), so keeping both
                    would be two copies of one control fighting for the right-hand edge.
                    §24.3.3 keeps the whole rail off a read-only page, which is the rule for
                    every other editing control.

                    Every entry comes from `PANEL_DEFS`, the three content panels (diagram,
                    gantt, chart) included: they stay VISIBLE with nothing to edit and go
                    disabled instead, because a rail button that appears and disappears as you
                    click around the canvas is worse than one that is always there in the same
                    place — and a button that opens a panel only to say "not here" is worse
                    than one that says so without being clicked. */}
                  {panel === null && !readOnly && (
                    <PanelRail onPick={setPanel} />
                  )}
                </div>
              </ChartEditingProvider>
            </GanttEditingProvider>
          </DiagramEditingProvider>
        </ThumbnailScheduleProvider>
      </SnapshotRegisterProvider>

      {/* §5.6 Fullscreen presentation overlay — entered from the TopBar, starting at the
          first active slide. Mounted over the (still-live) editor, so §5.7's "exit returns
          to the edit view at the right scroll position" falls out for free. Guarded on
          having something to present. */}
      {presenting && active.length > 0 && (
        <PresentationView
          slides={active}
          transition={transition}
          snapshots={snapshots}
          onExit={() => {
            setPresenting(false);
            setSnapshots({});
          }}
        />
      )}

      {/* §6 Export-to-PDF print layer. Mounts all active slides in a body-level portal and
          drives the browser print dialog; the print stylesheet hides the rest of the app.
          Grids render from the pre-captured `snapshots` (§6.6). Unmounts once printing
          finishes, dropping the snapshots. */}
      {printing && (
        <PrintView
          slides={active}
          title={deckTitle}
          snapshots={snapshots}
          capture={desktopShell() ? capturePdf : undefined}
          onFinished={() => {
            setPrinting(false);
            setSnapshots({});
          }}
        />
      )}

      {/* Off-screen full-slide thumbnail capturer. Sits OUTSIDE the snapshot/thumbnail
          providers (like the present/print overlays) so its off-screen ContentWindows never
          register embed handles or schedule grid captures. Only captures while the thumbnail
          view is showing and no stylesheet draft is being previewed. */}
      <OffscreenSlideCapturer
        items={active.map((s, i) => ({
          slide: s,
          index: i,
          pageNumber: numbers[i],
        }))}
        gridSnapshots={thumbnails}
        styleSheet={committedStyleSheet}
        enabled={navMode === "thumbnail" && !styleDirty}
        thumbs={slideThumbs}
        onCaptured={handleSlideCaptured}
      />
    </div>
  );
}
